<template>

  <div>

    <div class="row align-items-center">

      <div class="col" :class="filters.length > 0 ? 'col-7' : 'col-12'">
        <auto-complete v-if="canSearch"
          :placeHolder="placeHolder"
          @onTyped="onEntitySearch"
          :loading="loading"
          :searchParams="tableOptions.searchParams"
          @onParamChanged="setSearchParam"
      ></auto-complete>
      </div>

      <!-- FILTERS -->
      <div class="col justify-content-end entityPickerFilters" align="right">

        <el-dropdown @command="setActiveFilters" v-if="filters.length > 0">
          <span class="el-dropdown-link">
            <i @click="resetFilters" :class="` mr-1 fas fa fa-${activeFilters.length == 0 ? 'filter' : 'undo'}`"></i> Filters ({{activeFilters.length }})<i class="el-icon-arrow-down el-icon--right"></i>
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item :command="filter.name" v-for="filter in filters" :key="filter.name">
	<i :class="`text-${activeFilters.includes(filter.name) ? 'primary' : 'clear'} fas fa fa-${activeFilters.includes(filter.name) ? 'check-circle' : 'circle'}`"></i> {{filter.label}}
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>

      <div class="col col-1" align="right">
        <base-dropdown menuClasses="dropdown-menu-right" v-if="(!canSelect && canRefresh) || canExport">
            <base-button slot="title" type="neutral" class="dropdown-toggle btn-sm">
                <i class="fas fa fa-ellipsis-v"></i>
            </base-button>
            <a v-if="!canSelect && canRefresh" class="dropdown-item" href="javascript:void(0)" @click="fetchResults">Refresh</a>
            <a v-if="canExport" class="dropdown-item" href="javascript:void(0)" @click="showCSVExport = true">Export</a>
        </base-dropdown>
      </div>

    </div>

    <div class="d-flex" :class="{ 'justify-content-end': countDirection === 'right' }" v-if="showCount">
      <span>Total: {{ options.length }}</span>
    </div>
    <el-table
      element-loading-background="rgba(255, 255, 255, 0.8)!important"
      v-loading="loading"
      :class="{ 'mt-4': !showCount, 'mt-2': !!showCount }"
      class="table-responsive table-flush"
      header-row-class-name="thead-light"
      row-key="id"
      @sort-change="onTableSort"
      :data="options"
      v-bind="tableOptions"
      >

        <el-table-column :width="column.width || 'auto'" :formatter="formatter" v-for="column in tableColumns" :key="column.prop" :label="column.label" :prop="column.prop" :sortable="column.sortable">
            <template v-slot="{ row }" v-if="column.prop == 'dateDeployed'">
                {{row.dateDeployed}}
                <el-button size="mini" @click="toggleEditTrapDeployments(row)" style="margin-left: 5px">EDIT</el-button>
            </template>

            <template v-slot="{ row }" v-else-if="column.prop == 'dateRemoved'">
                {{row.dateRemoved}}
                <el-button size="mini" @click="toggleEditTrapRemovedOn(row)" style="margin-left: 5px">EDIT</el-button>
            </template>

            <template v-slot="{ row }" v-else-if="column.prop === 'status'">
	<simple-status-block :organisation="row" />
            </template>
        </el-table-column>

        <el-table-column min-width="120px" label="" align="center" class="pr-0">
            <template v-slot="{ row }">
                <div class="d-flex justify-content-end">
                    <router-link v-if="canEdit" class="btn btn-default btn-sm" :to="`${editPath}${row.id}`">
                      <i class="fas fa fa-pencil"></i>
                    </router-link>
                    <base-button class="btn-sm" v-if="canSelect && isEntitySelectable(row)" :type="`${isEntitySelected(row) ? 'danger' : 'success'}`" @click="handleSelect(row)">
                      <i :class="`fas fa fa-${isEntitySelected(row) ? 'minus' : 'plus'}`"></i>
                    </base-button>
                </div>
            </template>
        </el-table-column>

    </el-table>

    <div class="mt-4" align="center" v-if="cursor">
      <el-pagination
        :page-size="tableOptions.limit"
        :pager-count="5"
        layout="prev,pager, next"
        :hide-on-single-page="true"
        @current-change="handlePageChange"
        :page-count="cursor.pages.total">
      </el-pagination>
    </div>

    <div class="mt-3" align="right" v-if="canSelect && !hideSave">
      <base-button type="primary" @click="handleSave" :disabled="updateDisabled()">
          Save changes
      </base-button>
    </div>

    <modal :show.sync="showCSVExport">
        <h6 slot="header" class="modal-title">Export</h6>

        <base-alert>
            You will download this dataset ({{cursor.items}} lines) as a .csv file
        </base-alert>

        <template slot="footer">
            <base-button type="blank" @click="showCSVExport = false">Cancel</base-button>
            <base-button type="success" @click="exportToCSV">Go ahead</base-button>
        </template>
    </modal>

    <modal :show.sync="showEditTrapDeployments">
        <h6 slot="header" class="modal-title">Edit trap deployment</h6>

        <el-alert style="margin-bottom:2rem" type="warning" :closable="false" :show-icon="true">
          Be careful when using this feature as no safety mechanism exists to check past ownership!
          <br/><br/>
          The Deployed on date is in UTC time, which may differ from your local time. Please make any necessary adjustments.
        </el-alert>
        <el-alert style="margin-bottom:2rem" type="warning" :closable="false" :show-icon="true">
          WARNING: The Deployed on date is in UTC timezone, which is different than the timezone where the sensor/management unit is. It is important that DEPLOYED ON and REMOVED ON columns reflect correct dates and times when sensor was deployed. You MUST adjust the deployment time to be in UTC.
          <br/> <br/>
          For example, if sensor is being deployed to/removed from MU in Australia/Brisbane (that is 10 hours ahead of UTC), and the sensor was deployed 2024/09/24 12:20:00 the correct value to use is 2024/09/24 02:20:00. 
          <br/> <br/>
          Likewise, if the sensor is being deployed to/removed from MU in US/Pacific (that is 7 hours behind UTC), and the sensor was deployed 2024/09/24 12:20:00 the correct value to use is 2024/09/24 19:20:00. 
        </el-alert>
        <el-form-item label="Deployment" v-if="editedTrap != null">
          <b-col cols="16">
            <el-date-picker value-format="timestamp" v-model="editedTrapDeploymentStart" type="datetime" format="dd/MM/yyyy HH:mm" placeholder="Deployed on" style="width: 100%;"></el-date-picker>
          </b-col>

        </el-form-item>

        <template slot="footer">
            <base-button type="blank" @click="showEditTrapDeployments = false; editedTrap = null">Cancel</base-button>
            <base-button type="primary" :disabled="editedTrapDeploymentStart == null" @click="doEditTrapDeployment">Update</base-button>
        </template>
    </modal>

    <modal :show.sync="showEditTrapRemovedOn">
        <h6 slot="header" class="modal-title">Edit trap removed on</h6>
        <el-alert style="margin-bottom:2rem" type="info" :closable="false" :show-icon="true">
          A removed on date indicates that the sensor has been removed from the management unit, and any detections after this date won't be shown in sensor or management unit activity.
          <br/><br/>
          If a sensor was incorrectly marked as removed on the page, the 'Clear removed on date' button resets the date indicating the sensor is <strong>still deployed</strong> in the Management Unit, and detections will be visible.
          <br/> <br/>
          The Removed on date is in UTC time, which may differ from your local time. Please make any necessary adjustments. 
        </el-alert>
        <el-alert style="margin-bottom:2rem" type="warning" :closable="false" :show-icon="true">
          WARNING: The Removed on date is in UTC timezone, which is different than the timezone where the sensor/management unit is. It is important that DEPLOYED ON and REMOVED ON columns reflect correct dates and times when sensor was deployed. You MUST adjust the deployment time to be in UTC.
          <br/> <br/>
          For example, if sensor is being deployed to/removed from MU in Australia/Brisbane (that is 10 hours ahead of UTC), and the sensor was deployed 2024/09/24 12:20:00 the correct value to use is 2024/09/24 02:20:00. 
          <br/> <br/>
          Likewise, if the sensor is being deployed to/removed from MU in US/Pacific (that is 7 hours behind UTC), and the sensor was deployed 2024/09/24 12:20:00 the correct value to use is 2024/09/24 19:20:00. 
        </el-alert>
        <el-form-item label="Deployment" v-if="editedTrap != null">
          <b-col cols="24">
            <el-date-picker value-format="timestamp" v-model="formattedRemovedOn" type="datetime" format="dd/MM/yyyy HH:mm" placeholder="Removed on" :picker-options="removedOnPickerOptions" style="width: 100%;"></el-date-picker>
          </b-col>
        </el-form-item>

        <template slot="footer">
            <base-button type="blank" @click="showEditTrapRemovedOn = false">Cancel</base-button>
            <base-button type="warning" :disabled="formattedRemovedOn == null" @click="doReAddSensor">Clear removed on date</base-button>
            <base-button type="primary" :disabled="formattedRemovedOn == null" @click="doEditTrapRemovedOn">Update</base-button>
        </template>
    </modal>

  </div>

</template>

<script>

import api from '@/api';
import { TimeHelper } from '@/helpers/TimeHelper';
import { PersistentListFilter } from '@/helpers/PersistentListFilter';
import { SensorModel } from '@/models/sensor/sensor';
import { ReportModel } from '@/models/report/report';
import { UserModel } from '@/models/user/user';
import { SensorOrchardModel } from '@/models/sensor/sensorOrchard';
import { ToCSV } from '@/helpers/ToCSV';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Pagination,
  Popover,
  Table,
  TableColumn,
  Tooltip
} from 'element-ui';
import SimpleStatusBlock from '../Simple/SimpleStatusBlock.vue';

export default {
  name: 'entity-picker',
  props: {
    placeHolder: {
      type: String,
      default: 'Start typing',
      description: ''
    },
    tableColumns: {
      type: Array,
      default () {
        return [{
          prop: 'name',
          label: 'Name'
        }]
      },
      description: ''
    },
    selected: {
      type: Array,
      default: [],
      description: ''
    },
    filters: {
      type: Array,
      default () {
        return []
      },
      description: ''
    },
    multiple: {
      type: Boolean,
      default: false,
      description: ''
    },
    canExport: {
      type: Boolean,
      default: false,
      description: ''
    },
    canSearch: {
      type: Boolean,
      default: true,
      description: ''
    },
    canEdit: {
      type: Boolean,
      default: false,
      description: ''
    },
    canRefresh: {
      type: Boolean,
      default: true,
      description: ''
    },
    canSelect: {
      type: Boolean,
      default: true,
      description: ''
    },
    editPath: {
      type: String,
      default: '',
      description: ''
    },
    canBeEmpty: {
      type: Boolean,
      default: false,
      description: ''
    },
    apiEndpoint: {
      type: String,
      default: '',
      description: ''
    },
    rootProp: {
      type: String,
      default: ''
    },
    cursorData: {
      type: Object,
      default: function() {
        return {
          items: 0,
          pages: {
            total: 0
          }
        }
      }
    },
    tableOptions: {
      type: Object,
      default: function() {
        return  {
          "default-sort": {
            prop: 'name',
            order: "asc"
          },
          "searchParams": {
            default: "name"
          }
        }
      }
    },
    hideSave: {
      type: Boolean,
      default: false
    },
    showCount: {
      type: Boolean,
      default: false
    },
    countDirection: {
      type: String,
      default: 'left'
    }
  },
  computed: {
    formattedRemovedOn: {
      get() {
        return this.editedTrapRemovedOn ? new Date(this.editedTrapRemovedOn) : null;
      },
      set(value) {
        this.editedTrapRemovedOn = value ? new Date(value).getTime() : null;
      }
    }
  },
  components: {
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Pagination.name]: Pagination,
    [Checkbox.name]: Checkbox,
    [Popover.name]: Popover,
    [Button.name]: Button,
    [DatePicker.name]: DatePicker,
    [Alert.name]: Alert,
    SimpleStatusBlock
  },
  data() {
    return {
        loading: false,
        options: [],
        activeFilters: [],
        searchLabel: '',
        currentSorting: null,
        showCSVExport: false,
        searchParams: {},
        editedTrap: null,
        editedTrapDeploymentStart: null,
        editedTrapRemovedOn: null,
        showEditTrapDeployments: false,
        showEditTrapRemovedOn: false,
        activeSearchParam: this.tableOptions.searchParams.default,
        timeHelper: TimeHelper,
        persistentFilter: null,
        cursor: this.cursorData,
        removedOnPickerOptions: null,
    };
  },
  created: function() {
    this.options = this.modelResults(this.selected)
    this.currentSorting = this.tableOptions['default-sort']
    this.setPersistentFilters()
  },
  watch: {
    cursorData(val) {
      this.cursor = val;
    },
    cursor(val) {
      this.$emit('update:cursorData', val);
    }
  },
  methods: {
    setPersistentFilters() {
      this.persistentFilter = new PersistentListFilter(this.rootProp)
      if(this.persistentFilter.hasFilter()) {
        let filter = this.persistentFilter.retrieve()
        this.setActiveFilters(filter)
      }
    },
    doEditTrapDeployment: async function() {
    this.showEditTrapDeployments = false;
    this.$emit('loading', true);

    let payload = {
      sensorId: this.editedTrap.id
    };

    if(this.editedTrapDeploymentStart) {
      const utcDate = new Date(this.editedTrapDeploymentStart).toISOString();
      payload.addedOn = new Date(utcDate).getTime() / 1000;
    }

    await api.updateGroupSensors(this.editedTrap.groupId, [payload]);

    // Update the selected sensor with the updated date
    const sensor = this.selected.find((sensor) => sensor.id === payload.sensorId);
    if(sensor) {
      sensor.sensorAddedOn = payload.addedOn;
    }

    // Update the options that get rendered into the table
    this.options = this.modelResults(this.selected);

    this.$emit('onApiResult', "Trap deployment updated");
    this.$emit('update:selected', this.selected);
    this.editedTrapDeploymentStart = null;
    },
    doEditTrapRemovedOn: async function() {
      this.showEditTrapRemovedOn = false;
      this.$emit('loading', true);

      let payload = {
        sensorId: this.editedTrap.id
      };

      if(this.editedTrapRemovedOn) {
        const utcDate = new Date(this.editedTrapRemovedOn).toISOString();
        payload.removedOn = new Date(utcDate).getTime() / 1000;
      }

      await api.updateGroupSensors(this.editedTrap.groupId, [payload]);

      // Update the selected sensor with the updated date
      const sensor = this.selected.find((sensor) => sensor.id === payload.sensorId);
      if(sensor) {
        sensor.sensorRemovedOn = payload.removedOn;
      }

      // Update the options that get rendered into the table
      this.options = this.modelResults(this.selected);

      this.$emit('onApiResult', "Trap removed on updated");
      this.$emit('update:selected', this.selected);
      this.editedTrapRemovedOn = null;
    },
    doReAddSensor: async function() {
      let payload = {
        sensorId: this.editedTrap.id,
        removedOn: null
      }

      await api.addSensorsToGroup(this.editedTrap.groupId,[payload])
       // update the selected sensor with the updated date
       const sensor = this.selected.find((sensor) => sensor.id === payload.sensorId)
      if(sensor) {
        sensor.sensorRemovedOn = null
      }

      // update the options that get rendered into the table
      this.options = this.modelResults(this.selected)

      this.$emit('onApiResult', "Clear removed on date successful");
      this.$emit('update:selected', this.selected);
      this.editedTrapRemovedOn = null
      this.showEditTrapRemovedOn = false
    },
    toggleEditTrapDeployments: function(trap) {
      this.editedTrap = trap
      this.showEditTrapDeployments = true
    },
    toggleEditTrapRemovedOn: function(trap) {
      this.editedTrap = trap;
      this.showEditTrapRemovedOn = true;
      this.editedTrapRemovedOn = trap.sensorRemovedOn * 1000;

      this.removedOnPickerOptions = {
        disabledDate (date) {
          return date <= new Date(trap.sensorAddedOn * 1000);
        }
      };
    },
    setSearchParam: function(param) {
      this.activeSearchParam = param
    },
    handlePageChange: async function(newPage) {
      this.searchParams.page = newPage
      await this.fetchResults()
      delete this.searchParams.page
    },
    exportToCSV: async function() {
      // fetch full data set first
      let originalLimit = this.tableOptions.limit
      this.tableOptions.limit = 1000
      await this.fetchResults()
      this.tableOptions.limit = originalLimit

      let csvExport = new ToCSV(this.options)

      switch(this.rootProp) {
        case 'farms':
          csvExport.farmExport()
          break
        case 'users':
          csvExport.userExport()
          break
        case 'orchards':
          csvExport.orchardExport()
          break
        case 'sensors':
          csvExport.sensorExport()
          break
      }
      csvExport.convertToCSV()
      csvExport.openInBrowser()
      this.showCSVExport = false
    },
    onTableSort(params) {
      this.currentSorting = params
      this.fetchResults()
    },
    resetFilters() {
      this.persistentFilter.removeFilter()
      this.activeFilters = []
      this.searchParams = {}
      this.fetchResults()
    },
    formatter(row, column) {
      switch(column.property) {
        case 'active':
          return <span class={row.active ? 'badge badge-success' : 'badge badge-warning'}>{row.active ? 'active' : 'inactive'}</span>
        case 'status':
          return <span class={row.status == 'active' ? 'badge badge-success' : (row.status == 'pending' ? 'badge badge-warning' : 'badge badge-danger')}>{row.status !== 'deleted' ? row.status : 'disabled'}</span>
        case 'roles':
          return row.roles.join(', ')
        case 'currentStatus':
          return <i title={`${row.currentStatus.description}`} class={`text-${row.currentStatus.colour} fas fa fa-lg fa-${row.currentStatus.icon}`}></i>
        case 'createdOn':
          return TimeHelper.formatDate(row.createdOn)
        case 'orchardCount':
          if(row.orchardCount > 0)
            return <el-popover
	placement="right"
	title="Parent orchard(s)"
	width="200"
	trigger="hover"
	content={row.orchards.map((o) => o.name).join(' | ')}>
	<el-button slot="reference" size="mini">{row.orchardCount} parent orchard(s)</el-button>
            </el-popover>

          return <span class="badge badge-light">No parent orchard</span>
        case 'timeOfDay':
          return row.possibleHours[row.timeOfDay]
        case 'dayOfWeek':
          return row.dayOfWeek ? row.possibleDays[row.dayOfWeek] : '-'
        case 'deliveryType':
          return row.possibleDeliveryTypes[row.deliveryType]
        case 'day':
          return <i class={row.day ? 'text-yellow fas fa fa-sun fa-lg' : 'text-primary fas fa fa-moon fa-lg'}></i>
        case 'hasLocation':
          return <span class={row.hasLocation ? 'badge badge-success' : 'badge badge-warning'}>{row.hasLocation != '' ? 'yes' : 'no'}</span>
        case 'status':
          return <span class={row.status == 'released' ? 'badge badge-success' : 'badge badge-danger'}>{row.status}</span>
        case 'strength':
          return <span class={row.strength == 'weak' ? 'badge badge-danger' : (row.strength == 'medium' ? 'badge badge-warning' : 'badge badge-success')}>{row.strength}</span>
        case 'surveillance':
          return <span class={row.surveillance != null && row.surveillance != 'null' ? 'badge badge-success' : 'badge badge-danger'}>{row.surveillance != null && row.surveillance != 'null' ? 'yes' : 'no'}</span>
        case 'isSensorBiosecurity':
          return <span class={row.isSensorBiosecurity ? 'badge badge-success' : 'badge badge-danger'}>{row.isSensorBiosecurity ? 'yes' : 'no'}</span>

      }
      return row[column.property];
    },
    setActiveFilters: function(value) {
      // remove if already in list
      if(this.activeFilters.includes(value)) {
         this.activeFilters = this.activeFilters.filter((f) => f != value)

         if(this.activeFilters.length == 0) {
           this.persistentFilter.removeFilter()
         }
      }
      else {
        let cFilter = this.filters.filter((f) => f.name == value)[0]
        let sameFilters = this.filters.filter((f) => f.search == cFilter.search).map((f) => f.name)
        this.activeFilters = this.activeFilters.filter((f) => !sameFilters.includes(f))
        this.activeFilters.push(value)
        this.persistentFilter.persistFilter(value)

      }
      if(this.activeFilters.length > 0) {
        this.searchParams = {}
        let filterString = ''
        for(let i in this.activeFilters) {
          let currentFilter = this.filters.filter((f) => f.name == this.activeFilters[i])[0]
          if(currentFilter.param == undefined) continue
          let operator = ':'
          if(currentFilter.search == 'lastLogin') {
            operator = ':>'
            this.currentSorting.prop = 'lastLogin'
            this.currentSorting.direction = 'desc'
          }
          if(filterString != '') filterString = `${filterString},`
          filterString = `${filterString}${currentFilter.search}${operator}${currentFilter.param}`
        }
        this.searchParams['filter'] = filterString
      }
      else this.searchParams = {}
      this.fetchResults()
    },
    arrayUnique: function(array) {
        var a = array.concat();
        for(var i=0; i<a.length; ++i) {
            for(var j=i+1; j<a.length; ++j) {
                if(a[i].id == a[j].id)
                    a.splice(j--, 1);
            }
        }
        return a;
    },
    updateDisabled: function() {
      return this.selected.length === 0 && !this.canBeEmpty;
    },
    handleSave: function() {
      this.$emit('onSelect', this.selected);
    },
    onEntitySearch: function(value) {
      this.searchLabel = value;
      this.fetchResults()
    },
    isEntitySelectable(entity) {
      if(this.rootProp == 'sensors') {
        return entity.sensorRemovedOn == null
      }
      return true
    },
    isEntitySelected(entity) {
      return this.selected.filter((item) => item.id == entity.id).length > 0
    },
    handleSelect: function(entity) {

      if(this.isEntitySelected(entity)) {
        this.selected = this.selected.filter((item) => item.id != entity.id)

        if(this.hideSave) {
          this.$emit('update:selected', this.selected);
        }

        this.$emit('onChange', this.selected);
        return
      }

      if(this.multiple) {
        this.selected.push(entity)
      }

      if(!this.multiple) {
        this.selected = [entity]
      }

      if(this.hideSave) {
        this.$emit('update:selected', this.selected);
      }

      this.$emit('onChange', this.selected);
    },
    sortPropToQueryObject: function() {
      return {
        sort: this.currentSorting.prop,
        direction: this.currentSorting.order == 'ascending' ? 'asc' : 'desc',
        limit: this.tableOptions.limit || 10
      }
    },
    getNameFilterByEntityType() {
      switch(this.rootProp) {
        case 'sensors':
          return 'barcode'
        default:
          return 'name'
      }
    },
    fetchResults: async function() {
        this.loading = true;
        let sortParams = this.currentSorting.order != null ? this.sortPropToQueryObject() : {}
        let params = {...this.searchParams};
        if(this.searchLabel != '') {
          let searchLabelParam = `${this.activeSearchParam}:${this.searchLabel}`
          params.filter += params.filter != '' ? `,${searchLabelParam}` : searchLabelParam;
        }

        params = {...params, ...sortParams}
        const results = await api[this.apiEndpoint](params)
        this.options = []
        this.options = this.canSelect ? this.arrayUnique(this.modelResults(results[this.rootProp]).concat(this.selected)) : this.modelResults(results[this.rootProp])

        this.cursor = results.cursor
        this.loading = false;
    },
    modelResults(results) {
      let output
      switch(this.rootProp) {
        case 'sensors':
          output = results.map((r) => new SensorModel(r))
          return output;
        case 'users':
          output = results.map((r) => new UserModel(r))
          return output;
        case 'sensorOrchards':
          output = results.map((r) => new SensorOrchardModel(r))
          return output;
        case 'reports':
          output = results.map((r) => new ReportModel(r))
          return output;
      }
      return results
    }
  }
}
</script>

